import React, { useCallback, useEffect, useState } from 'react';
import { Key } from 'ts-key-enum';

import { IngestionArtifactModel, IngestionArtifactType, IngestionResourceModel } from '../../../../../../gamma-api';
import IbSpin from '../../../../../components/common/IbSpin';
import ResizableDrawer from '../../../../../components/common/ResizableDrawer';
import ContentSplitView from '../../../../../components/ContentSplitView';
import ArtifactListTitle from '../ArtifactListTitle';

import './index.less';

import { MAIN_CLASS_NAME } from './const';

enum ContentRenderMode {
  MARKDOWN = 'markdown',
  PLAINTEXT = 'plaintext',
  BOTH = 'both',
}

export interface IArticleListProps {
  visible: boolean;
  loading: boolean;
  resource?: IngestionResourceModel;
  items: IngestionArtifactModel[];
  onOpen?: (
    resource: IngestionResourceModel,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => void;
  onClose?: (artifactType: IngestionArtifactType) => void;
  onResourceNext?: (currentResource: IngestionResourceModel, artifactType: IngestionArtifactType) => void;
  onResourcePrevious?: (currentResource: IngestionResourceModel, artifactType: IngestionArtifactType) => void;
}

const ArticleList: React.FC<IArticleListProps> = ({
  visible,
  loading,
  resource,
  items,
  onOpen,
  onClose,
  onResourceNext,
  onResourcePrevious,
}) => {
  const [renderMode, setRenderMode] = useState(ContentRenderMode.MARKDOWN);

  const onNextResourceClick = useCallback(() => {
    resource && onResourceNext?.(resource, IngestionArtifactType.Article);
  }, [resource, onResourceNext]);

  const onPreviousResourceClick = useCallback(() => {
    resource && onResourcePrevious?.(resource, IngestionArtifactType.Article);
  }, [resource, onResourcePrevious]);

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === Key.ArrowRight) {
        e.preventDefault();
        onNextResourceClick();
      } else if (e.key === Key.ArrowLeft) {
        e.preventDefault();
        onPreviousResourceClick();
      } else if (e.key === 'c'.toLowerCase() && !e.ctrlKey) {
        e.preventDefault();
        resource && onOpen?.(resource, IngestionArtifactType.Fragment, IngestionArtifactType.Article);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onNextResourceClick, onPreviousResourceClick, onOpen]);

  const renderTitle = () => {
    const onRenderModeChange = (value: string) => setRenderMode(value as ContentRenderMode);

    return (
      <ArtifactListTitle
        artifactType={IngestionArtifactType.Article}
        renderMode={renderMode}
        resource={resource}
        onNextResourceClick={onNextResourceClick}
        onOpen={onOpen}
        onPreviousResourceClick={onPreviousResourceClick}
        onRenderModeChange={onRenderModeChange}
      />
    );
  };

  const renderItem = (item: IngestionArtifactModel) => {
    return <ContentSplitView renderMode={renderMode} text={item.content.data} />;
  };

  const renderContent = () => {
    if (loading) {
      return <IbSpin />;
    }

    return items.map(renderItem);
  };

  const onDrawerClose = () => {
    onClose?.(IngestionArtifactType.Article);
  };

  return (
    <ResizableDrawer
      resizable
      className={MAIN_CLASS_NAME}
      title={renderTitle()}
      visible={visible}
      width="75%"
      onClose={onDrawerClose}
    >
      {renderContent()}
    </ResizableDrawer>
  );
};

export default ArticleList;
